import {
    Grid,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    IconButton,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    Avatar,
    useMediaQuery,
    Box
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NavigatedComponent from "../NavigatedComponent";
import Header from "../../common/Header";
import Sidenav from "../../common/Sidenav";
import IconSidenav from "../../common/IconSidenav";
import { Edit, Delete, Add } from "@mui/icons-material";
import { apiList, invokeApi } from "../../../services/apiServices";
import { config } from "../../../config/config";
import { useCookies } from "react-cookie";

const PdfList = () => {
    const [pdfs, setPdfs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteId, setDeleteId] = useState(null);
    const [cookies] = useCookies();
    const navigate = useNavigate();
    const location = useLocation();
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [showSideNav, setShowSideNav] = useState(false);

    const isMobileScreen = useMediaQuery("(min-width:360px) and (max-width:500px)");

    const fetchPdfs = async () => {
        setLoading(true);
        try {
            const response = await invokeApi(config.getMyCollege + apiList.getPdfPublications, {}, cookies);
            if (response?.status === 200 && response?.data?.pdfs) {
                setPdfs(response.data.pdfs);
            } else {
                setPdfs([]);
            }
        } catch (error) {
            console.error("Error fetching PDFs:", error);
            setSnackbar({ open: true, message: "Failed to fetch PDFs", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPdfs();
    }, []);

    const toggleSideNav = () => {
        setShowSideNav(!showSideNav);
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            const response = await invokeApi(
                config.getMyCollege + apiList.deletePdfPublication,
                { id: deleteId },
                cookies
            );
            if (response?.status >= 200 && response?.status < 300) {
                setSnackbar({ open: true, message: "Publication deleted successfully", severity: "success" });
                fetchPdfs();
            } else {
                setSnackbar({ open: true, message: "Failed to delete publication", severity: "error" });
            }
        } catch (error) {
            console.error("Error deleting publication:", error);
            setSnackbar({ open: true, message: "Error deleting publication", severity: "error" });
        } finally {
            setDeleteId(null);
        }
    };

    return (
        <Grid sx={{ display: "flex" }}>
            {isMobileScreen ? (
                <IconSidenav />
            ) : showSideNav ? (
                <IconSidenav />
            ) : (
                <Sidenav />
            )}
            <Grid component="main" sx={{ width: "100%", px: 1 }}>
                <Header toggleSideNav={toggleSideNav} />
                <Grid sx={{ py: 1 }}>
                    <NavigatedComponent pathname={location.pathname} />
                    <Grid sx={{ width: "100%", px: isMobileScreen ? 1 : 5, py: 3 }}>
                        <Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="h5" fontWeight="bold">Reports & PDFs</Typography>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => navigate("/publication-manager/add-pdf")}
                                sx={{ bgcolor: "#262272" }}
                            >
                                Add Report/PDF
                            </Button>
                        </Grid>

                        <Grid item xs={12}>
                            {loading ? (
                                <CircularProgress />
                            ) : (
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                                            <TableRow>
                                                <TableCell><strong>Image</strong></TableCell>
                                                <TableCell><strong>Title</strong></TableCell>
                                                <TableCell><strong>Category</strong></TableCell>
                                                <TableCell><strong>Subcategory</strong></TableCell>
                                                <TableCell><strong>PDF Link</strong></TableCell>
                                                <TableCell align="right"><strong>Actions</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {pdfs.length > 0 ? (
                                                pdfs.map((item) => (
                                                    <TableRow key={item.id}>
                                                        <TableCell>
                                                            <Avatar src={item.imageUrl} variant="rounded" sx={{ width: 50, height: 70 }} />
                                                        </TableCell>
                                                        <TableCell>{item.title}</TableCell>
                                                        <TableCell>{item.categoryName || item.category || "-"}</TableCell>
                                                        <TableCell>{item.subcategoryName || item.subcategory || "-"}</TableCell>
                                                        <TableCell>
                                                            <a href={item.pdfUrl} target="_blank" rel="noopener noreferrer">View PDF</a>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <IconButton color="primary" onClick={() => navigate(`/publication-manager/edit-pdf/${item.id}`)}>
                                                                <Edit />
                                                            </IconButton>
                                                            <IconButton color="error" onClick={() => setDeleteId(item.id)}>
                                                                <Delete />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center">No reports found</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Grid>

                        {/* Delete Confirmation Dialog */}
                        <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
                            <DialogTitle>Confirm Delete</DialogTitle>
                            <DialogContent>Are you sure you want to delete this publication?</DialogContent>
                            <DialogActions>
                                <Button onClick={() => setDeleteId(null)}>Cancel</Button>
                                <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
                            </DialogActions>
                        </Dialog>

                        <Snackbar
                            open={snackbar.open}
                            autoHideDuration={6000}
                            onClose={() => setSnackbar({ ...snackbar, open: false })}
                        >
                            <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                                {snackbar.message}
                            </Alert>
                        </Snackbar>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default PdfList;
